import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import { useActiveContextReady } from './useActiveContextReady';
import { config } from '../config';

// Wire types — kept verbose to mirror the backend DeviceStpResponse
// view exactly. The view enforces camelCase on the wire, so these
// match field-for-field with no transformer in between.

export interface DeviceStpInstance {
  id: string;
  instanceId: number;
  instanceType: string;
  vlanId: number | null;
  bridgePriority: number;
  bridgeAddress: string;
  bridgeId: string;
  rootPriority: number;
  rootAddress: string;
  rootId: string;
  rootPort: number | null;
  rootPathCost: number;
  helloTime: number | null;
  maxAge: number | null;
  forwardDelay: number | null;
  protocolVersion: string | null;
  /** null: the source reports no count (a UniFi controller states none). Not 0. */
  topologyChanges: number | null;
  lastTopologyChange: string | null;
  isRootBridge: boolean;
  operationalStatus: string | null;
}

export interface DeviceStpInterfaceState {
  id: string;
  interfaceId: string;
  interfaceName: string | null;
  instanceId: number;
  portNumber: number;
  portPriority: number;
  portId: string;
  portState: string;
  portRole: string | null;
  adminStatus: string;
  adminEdgePort: boolean;
  operEdgePort: boolean;
  adminPathCost: number | null;
  operPathCost: number;
  designatedRootId: string | null;
  designatedBridgeId: string | null;
  designatedPortId: string | null;
  designatedCost: number | null;
  pointToPoint: string | null;
  autoEdgePort: boolean;
  bpduGuardEnabled: boolean;
  bpduGuardViolated: boolean;
  rootGuardEnabled: boolean;
  rootGuardViolated: boolean;
  loopGuardEnabled: boolean;
  loopGuardViolated: boolean;
  bpduFilterEnabled: boolean;
}

export interface DeviceMstpVlanMapping {
  instanceId: number;
  vlanId: number;
}

export interface DeviceMstpRegion {
  id: string;
  regionName: string;
  revisionLevel: number;
  configurationDigest: string | null;
  cistRootId: string | null;
  cistRegionalRootId: string | null;
  cistPathCost: number | null;
  vlanMappings: DeviceMstpVlanMapping[];
}

export interface DeviceStpInconsistency {
  id: string;
  kind: string;
  instanceId: number | null;
  portNumber: number | null;
  severityHint: string;
  detail: string | null;
  dedupeKey: string;
  detectedAt: string;
  lastSeenAt: string;
}

export interface DeviceStpResponse {
  deviceId: string;
  instances: DeviceStpInstance[];
  interfaceStates: DeviceStpInterfaceState[];
  mstpRegion: DeviceMstpRegion | null;
  inconsistencies: DeviceStpInconsistency[];
}

export function useDeviceStp(deviceId: string | undefined) {
  const contextReady = useActiveContextReady();
  return useQuery<DeviceStpResponse>({
    queryKey: ['deviceStp', deviceId],
    queryFn: async () => {
      if (!deviceId) throw new Error('Device ID is required');
      const response = await axios.get<DeviceStpResponse>(
        `${config.backendApi}/api/devices/${deviceId}/stp`,
        { withCredentials: true }
      );
      return response.data;
    },
    enabled: !!deviceId && contextReady,
  });
}
